import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { naturalCompare } from '~/pages/AdminPlantUnitMaster/models/unitRows'

type SiteOption = { id: string; siteName: string; areaCode: AreaCode }

/**
 * 発電所の候補: エリアで絞り (UNSPECIFIED = 絞らない)、名称の自然順に並べる。
 * 今選ばれている発電所は、別エリアでも残す (選択状態を保つ)。
 */
export function siteCandidates<T extends SiteOption>(
  sites: readonly T[],
  options: { areaCode: AreaCode; currentSiteId: string },
): T[] {
  return sites
    .filter(
      (site) =>
        options.areaCode === AreaCode.UNSPECIFIED ||
        site.areaCode === options.areaCode ||
        site.id === options.currentSiteId,
    )
    .sort((a, b) => naturalCompare(a.siteName, b.siteName))
}

type PlantOption = { id: string; plantCode: string; areaCode: AreaCode }

/** 文字コード順 (系統コードの並び)。 */
function ordinalCompare(a: string, b: string): number {
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

/**
 * 系統コード (広域機関の発電所) の候補: エリアで絞り (UNSPECIFIED = 絞らない)、系統コードの
 * 文字コード順に並べる。今の系統コードは、別エリアでも残す (選択状態を保つ)。
 */
export function plantCandidates<T extends PlantOption>(
  plants: readonly T[],
  options: { areaCode: AreaCode; currentPlantMasterId: string },
): T[] {
  return plants
    .filter(
      (plant) =>
        options.areaCode === AreaCode.UNSPECIFIED ||
        plant.areaCode === options.areaCode ||
        plant.id === options.currentPlantMasterId,
    )
    .sort((a, b) => ordinalCompare(a.plantCode, b.plantCode))
}

/**
 * 登録ダイアログでエリアを変えた時に外す選択を決める: 選んでいた発電所 / 系統コードのエリアが
 * 新しいエリアと違えば外す。エリアを未選択に戻した時は何も外さない。
 * `selectedSiteArea` / `selectedPlantArea` は未選択・不明なら undefined。
 */
export function selectionsToClearOnAreaChange(input: {
  areaCode: AreaCode
  selectedSiteArea: AreaCode | undefined
  selectedPlantArea: AreaCode | undefined
}): { clearSite: boolean; clearPlant: boolean } {
  if (input.areaCode === AreaCode.UNSPECIFIED) {
    return { clearSite: false, clearPlant: false }
  }
  return {
    clearSite:
      input.selectedSiteArea !== undefined &&
      input.selectedSiteArea !== input.areaCode,
    clearPlant:
      input.selectedPlantArea !== undefined &&
      input.selectedPlantArea !== input.areaCode,
  }
}

/**
 * 詳細の編集で発電所を変えた時に系統コードの選択を外すか: 新しい発電所のエリアと、選んでいた
 * 系統コードのエリアが違えば外す。
 */
export function shouldClearPlantOnSiteChange(
  newSiteArea: AreaCode | undefined,
  selectedPlantArea: AreaCode | undefined,
): boolean {
  return (
    newSiteArea !== undefined &&
    selectedPlantArea !== undefined &&
    newSiteArea !== selectedPlantArea
  )
}
